import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLocation, useNavigate, Navigate, Link } from "react-router-dom";
import { LayoutDashboard, BookOpen, Archive, Heart, AlertCircle, BarChart3, User, LogOut, Repeat, Trophy, MoreHorizontal, ChevronRight, Camera, Compass, Crown, Sparkles, Image as ImageIcon } from "lucide-react";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "../components/ui/sheet";
import NotificationBell from "../components/NotificationBell";
import QuickScanDialog from "../components/QuickScanDialog";
import PWAInstallPrompt from "../components/PWAInstallPrompt";
import { isPremium, premiumDaysRemaining } from "../lib/premium";

const mainNav = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard" },
  { icon: BookOpen, label: "Catalog", path: "/catalog" },
  { icon: Archive, label: "My Collection", path: "/collection" },
  { icon: ImageIcon, label: "Showcases", path: "/showcases" },
  { icon: Heart, label: "Wishlist", path: "/wishlist" },
  { icon: Compass, label: "Discover", path: "/discover" },
  { icon: Repeat, label: "Trade Board", path: "/trades" },
  { icon: AlertCircle, label: "Missing", path: "/missing" },
  { icon: BarChart3, label: "Analytics", path: "/analytics" },
  { icon: Trophy, label: "Leaderboard", path: "/leaderboard" },
];

const mobileNav = [
  { icon: LayoutDashboard, label: "Home", path: "/dashboard" },
  { icon: BookOpen, label: "Catalog", path: "/catalog" },
  { icon: Archive, label: "Collection", path: "/collection" },
  { icon: ImageIcon, label: "Showcases", path: "/showcases" },
  { icon: Heart, label: "Wishlist", path: "/wishlist" },
];

const moreMenuItems = [
  { icon: Sparkles, label: "Upgrade to Premium", path: "/billing", color: "text-amber-400" },
  { icon: ImageIcon, label: "Showcases", path: "/showcases", color: "text-rose-400" },
  { icon: Camera, label: "Shelf-scan", path: "/shelf-scan", color: "text-amber-400" },
  { icon: Compass, label: "Discover", path: "/discover", color: "text-rose-400" },
  { icon: BarChart3, label: "Analytics", path: "/analytics", color: "text-sky-400" },
  { icon: Trophy, label: "Leaderboard", path: "/leaderboard", color: "text-amber-400" },
  { icon: Repeat, label: "Trade Board", path: "/trades", color: "text-emerald-400" },
  { icon: AlertCircle, label: "Missing Releases", path: "/missing", color: "text-rose-400" },
  { icon: User, label: "Profile", path: "/profile", color: "text-zinc-300" },
];

function isActive(pathname, path) {
  return pathname === path || (path !== "/dashboard" && pathname.startsWith(path));
}

function SidebarNav({ pathname, onLogout, user }) {
  const premium = isPremium(user);
  const days = premiumDaysRemaining(user);
  const isAdmin = user?.role === "admin";
  const billingLabel = isAdmin ? "Admin" : premium ? "Premium" : "Upgrade";
  const billingMeta = isAdmin ? "Full access" : premium ? `${days}d left` : "Pro";
  return (
    <aside data-testid="desktop-sidebar" className="w-64 fixed left-0 top-0 bottom-0 bg-zinc-950 border-r border-zinc-800/50 z-40 hidden md:flex flex-col">
      <div className="p-5 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="sidebar-logo">
          <img src="/logo.png" alt="Castfolio" className="w-10 h-10 rounded-lg object-cover" />
          <div>
            <h1 className="font-heading text-base font-bold text-zinc-100 leading-tight">Castfolio</h1>
            <p className="text-[10px] text-rose-500 uppercase tracking-[0.2em] font-medium">Portfolio</p>
          </div>
        </Link>
        <NotificationBell />
      </div>
      <Separator className="bg-zinc-800/50" />
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {mainNav.map((item) => {
            const Icon = item.icon;
            const active = isActive(pathname, item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  active
                    ? "bg-rose-600/10 text-rose-500 border border-rose-500/20"
                    : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/50"
                }`}
              >
                <Icon className="w-4.5 h-4.5" />
                {item.label}
              </Link>
            );
          })}
        </nav>
      </ScrollArea>
      <div className="p-3 border-t border-zinc-800/50">
        <Link
          to="/billing"
          data-testid="nav-billing"
          className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors mb-1 ${
            premium
              ? "bg-gradient-to-r from-amber-500/15 to-rose-500/10 text-amber-300 ring-1 ring-amber-400/20"
              : "bg-gradient-to-r from-amber-500/10 to-fuchsia-500/10 text-zinc-200 hover:from-amber-500/20 hover:to-fuchsia-500/20 ring-1 ring-amber-400/15"
          }`}
        >
          <span className="flex items-center gap-3">
            {premium ? <Crown className="w-4.5 h-4.5 text-amber-400" /> : <Sparkles className="w-4.5 h-4.5 text-amber-400" />}
            {billingLabel}
          </span>
          <span className="text-[10px] text-zinc-400">
            {billingMeta}
          </span>
        </Link>
        <Link
          to="/profile"
          data-testid="nav-profile"
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            isActive(pathname, "/profile") ? "bg-rose-600/10 text-rose-500" : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/50"
          }`}
        >
          <User className="w-4.5 h-4.5" />
          Profile
        </Link>
        <button
          onClick={onLogout}
          data-testid="logout-btn"
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-zinc-500 hover:text-rose-400 hover:bg-zinc-800/50 transition-colors w-full"
        >
          <LogOut className="w-4.5 h-4.5" />
          Logout
        </button>
      </div>
    </aside>
  );
}

function MobileMoreMenu({ pathname, onLogout, user }) {
  const [open, setOpen] = useState(false);
  // Active if the current path matches any item inside the More sheet
  const activeInMore = moreMenuItems.some((m) => isActive(pathname, m.path));

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid="mobile-nav-more"
          className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg transition-colors ${
            activeInMore ? "text-rose-500" : "text-zinc-500"
          }`}
        >
          <MoreHorizontal className="w-5 h-5" />
          <span className="text-[10px] font-medium">More</span>
        </button>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        data-testid="mobile-more-sheet"
        className="bg-zinc-950 border-t border-zinc-800 text-zinc-100 rounded-t-2xl max-h-[80vh] p-0"
      >
        <SheetHeader className="p-5 pb-3 border-b border-zinc-800/50">
          <SheetTitle className="font-heading text-zinc-100 text-lg text-left flex items-center justify-between">
            <span>More</span>
            <NotificationBell />
          </SheetTitle>
        </SheetHeader>

        {/* User chip */}
        <Link
          to="/profile"
          onClick={() => setOpen(false)}
          data-testid="more-user-chip"
          className="flex items-center gap-3 p-4 mx-4 mt-4 rounded-xl bg-zinc-900 border border-zinc-800 hover:border-zinc-700 transition-colors"
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-600 to-amber-500 flex items-center justify-center font-bold text-white text-sm">
            {(user?.name || "?").charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-zinc-100 truncate">{user?.name || "Collector"}</p>
            <p className="text-[11px] text-zinc-500 truncate">{user?.email}</p>
          </div>
          <ChevronRight className="w-4 h-4 text-zinc-500 flex-shrink-0" />
        </Link>

        <div className="p-4 space-y-1">
          {moreMenuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(pathname, item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setOpen(false)}
                data-testid={`more-link-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  active
                    ? "bg-rose-600/10 text-rose-400 border border-rose-500/20"
                    : "text-zinc-300 hover:bg-zinc-900 border border-transparent"
                }`}
              >
                <Icon className={`w-5 h-5 ${active ? "text-rose-400" : item.color}`} />
                <span className="flex-1">{item.label}</span>
                <ChevronRight className="w-4 h-4 text-zinc-600" />
              </Link>
            );
          })}

          <div className="pt-2 mt-2 border-t border-zinc-800/50">
            <button
              onClick={() => { setOpen(false); onLogout(); }}
              data-testid="more-logout"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-zinc-500 hover:text-rose-400 hover:bg-zinc-900 w-full"
            >
              <LogOut className="w-5 h-5" />
              <span className="flex-1 text-left">Logout</span>
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function MobileBottomNav({ pathname, onLogout, user }) {
  return (
    <nav
      data-testid="mobile-nav"
      className="fixed bottom-0 left-0 right-0 bg-zinc-950 border-t border-zinc-800 z-50 flex justify-around items-center md:hidden"
      style={{
        // Solid bg + iOS safe-area inset so content never bleeds behind the
        // bar on devices with a home indicator. Height stays h-16 visually.
        paddingBottom: "env(safe-area-inset-bottom)",
        height: "calc(4rem + env(safe-area-inset-bottom))",
      }}
    >
      {mobileNav.map((item) => {
        const Icon = item.icon;
        const active = isActive(pathname, item.path);
        return (
          <Link
            key={item.path}
            to={item.path}
            data-testid={`mobile-nav-${item.label.toLowerCase()}`}
            className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg transition-colors ${
              active ? "text-rose-500" : "text-zinc-500"
            }`}
          >
            <Icon className="w-5 h-5" />
            <span className="text-[10px] font-medium">{item.label}</span>
          </Link>
        );
      })}
      <MobileMoreMenu pathname={pathname} onLogout={onLogout} user={user} />
    </nav>
  );
}

export default function Layout({ children }) {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-rose-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-500 font-body text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || user === false) {
    return <Navigate to="/login" replace />;
  }

  const handleLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-zinc-950">
      <SidebarNav pathname={location.pathname} onLogout={handleLogout} user={user} />
      <main className="pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-0 md:pl-64 min-h-screen">
        {children}
      </main>
      <MobileBottomNav pathname={location.pathname} onLogout={handleLogout} user={user} />
      <QuickScanDialog
        trigger={
          <button
            data-testid="mobile-scan-fab"
            className="md:hidden fixed right-4 bottom-[84px] z-40 w-14 h-14 rounded-full bg-gradient-to-br from-rose-500 to-amber-500 shadow-lg shadow-rose-500/30 flex items-center justify-center text-white active:scale-95 transition-transform"
            aria-label="Quick scan"
          >
            <Camera className="w-6 h-6" />
          </button>
        }
      />
      <PWAInstallPrompt />
    </div>
  );
}
